import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useAppSelector } from '@state/hooks'
import { getNumericCompanyId } from '@utils/companyUtils'
import { useIsSampleCompany } from '@state/hooks/useIsSampleCompany'
import ExecutiveSummary from './review/ExecutiveSummary'
import RecapSection from './review/RecapSection'
import ScorecardSection from './review/ScorecardSection'
import CausalAuditSection from './review/CausalAuditSection'
import HealthCheckSection from './review/HealthCheckSection'
import InsightsSection from './review/InsightsSection'
import BlueprintSection from './review/BlueprintSection'
import DataSourcesPanel from './review/DataSourcesPanel'
import './ReviewTab.css'
import AdvisorBrandPanel from './review/AdvisorBrandPanel'
import { getAdvisorBrand, hasUsableBrand } from '@utils/advisorBrand'
import { exportCycleReviewPdf } from '@utils/cycleReviewPdfExport'

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000'

export interface InputSnapshot {
  schema_version: number
  captured_at: string
  cycle: { start: string; end: string }
  strategy_data: {
    session_count: number
    sessions: {
      date: string
      mode: string
      completed: boolean
      user_messages: string[]
      mece_pillars: unknown[]
      strategy_objectives: unknown[]
    }[]
  }
  execution: Record<string, unknown>
  execution_meta: { scoping: string }
  vitals: {
    as_of: string
    source: 'period_bound' | 'snapshot' | 'current_fallback' | 'mixed'
    snapshot_date: string | null
    current_vitals: {
      actuals: Record<string, number | null> & {
        _source?: Record<string, 'period_bound' | 'snapshot' | 'current_fallback' | 'error'>
      }
      data_quality: string
      metrics_computed: number
      computed_at: string
    }
    ivp_comparison: Record<string, unknown>
    ivp: Record<string, unknown> | null
    trajectory: { date: string; value: number }[]
  }
  prior_reviews: unknown[]
  dossier: Record<string, unknown> | null
  vigil: Record<string, unknown> | null
  data_coverage?: Record<
    string,
    { earliest: string | null; latest: string | null; count_in_cycle: number }
  >
}

interface CycleReview {
  id: number
  company_id: number
  cycle_start: string
  cycle_end: string
  review_data: any
  status: string
  created_at: string
  input_snapshot?: InputSnapshot | null
  input_fingerprint?: string | null
  drifted?: boolean | null
}

const V2_SECTIONS = [
  { key: 'recap', label: '1. Strategy Recap', icon: '\u{1F4CB}' },
  { key: 'scorecard', label: '2. Execution Scorecard', icon: '\u{1F4C8}' },
  { key: 'causal_audit', label: '3. Causal Chain Audit', icon: '\u{1F517}' },
  { key: 'health_check', label: '4. Company Health Check', icon: '\u{1F3E5}' },
  { key: 'insights', label: '5. Key Insights', icon: '\u{1F4A1}' },
  { key: 'blueprint', label: '6. Next Cycle Blueprint', icon: '\u{1F331}' },
] as const

const V1_SECTIONS = [
  { key: 'recap', label: '1. Strategy Recap', icon: '\u{1F4CB}' },
  { key: 'progress', label: '2. Execution Progress', icon: '\u{1F4C8}' },
  { key: 'execution_assessment', label: '3. Execution Assessment', icon: '\u2705' },
  { key: 'causal_chain_validation', label: '4. Causal Chain Validation', icon: '\u{1F517}' },
  { key: 'ivp_delta', label: '5. IVP Delta', icon: '\u{1F4CA}' },
  { key: 'lessons_learned', label: '6. Lessons Learned', icon: '\u{1F4A1}' },
  { key: 'next_cycle_seed', label: '7. Next Cycle Seed', icon: '\u{1F331}' },
] as const

function isV2Review(reviewData: any): boolean {
  return reviewData?.version === 2
}

function renderV2Section(key: string, data: any, props: {
  onStartNewCycle?: () => void
  startingCycle?: boolean
}) {
  if (!data) return <p className="review-empty">No data available for this section.</p>

  switch (key) {
    case 'recap': return <RecapSection data={data} />
    case 'scorecard': return <ScorecardSection data={data} />
    case 'causal_audit': return <CausalAuditSection data={data} />
    case 'health_check': return <HealthCheckSection data={data} />
    case 'insights': return <InsightsSection data={data} />
    case 'blueprint':
      return (
        <BlueprintSection
          data={data}
          onStartNewCycle={props.onStartNewCycle}
          startingCycle={props.startingCycle}
        />
      )
    default: return <pre className="review-json">{JSON.stringify(data, null, 2)}</pre>
  }
}

function renderV1Section(_key: string, data: any) {
  if (!data) return <p className="review-empty">No data available for this section.</p>
  if (typeof data === 'string') return <div className="review-text">{data}</div>
  return <pre className="review-json">{JSON.stringify(data, null, 2)}</pre>
}

function getGradePillClass(grade: string): string {
  const letter = (grade || '').charAt(0).toUpperCase()
  if (letter === 'A') return 'grade-a'
  if (letter === 'B') return 'grade-b'
  if (letter === 'C') return 'grade-c'
  return 'grade-d'
}

export default function ReviewTab() {
  const { activeCompanyId } = useAppSelector(state => state.companies)
  const companyId = activeCompanyId ? getNumericCompanyId(activeCompanyId) : null
  const isSampleCompany = useIsSampleCompany()

  const [reviews, setReviews] = useState<CycleReview[]>([])
  const [expandedReviewId, setExpandedReviewId] = useState<number | null>(null)
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['recap']))
  const [loading, setLoading] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [cycleStart, setCycleStart] = useState('')
  const [cycleEnd, setCycleEnd] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [startingCycle, setStartingCycle] = useState(false)
  const [rowBusyId, setRowBusyId] = useState<number | null>(null)
  const [rowError, setRowError] = useState<{ id: number; message: string } | null>(null)
  const [snapshotsById, setSnapshotsById] = useState<Record<number, InputSnapshot | null>>({})
  const [snapshotFetchingId, setSnapshotFetchingId] = useState<number | null>(null)
  const rowContentRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const [pdfExportingId, setPdfExportingId] = useState<number | null>(null)
  const [pdfErrorById, setPdfErrorById] = useState<Record<number, string>>({})
  const [brandPanelExpanded, setBrandPanelExpanded] = useState(!hasUsableBrand())

  const loadReviews = useCallback(async () => {
    if (!companyId) return
    setLoading(true)
    try {
      const token = localStorage.getItem('jwt_token')
      const res = await fetch(`${API_BASE}/reviews/${companyId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      })
      if (res.ok) {
        const data = await res.json()
        setReviews(data.reviews || [])
      }
    } catch (e) {
      console.error('Failed to load reviews:', e)
    } finally {
      setLoading(false)
    }
  }, [companyId])

  const fetchSnapshotForReview = useCallback(async (reviewId: number) => {
    if (!companyId) return
    if (snapshotsById[reviewId] !== undefined) return  // already fetched (may be null)
    setSnapshotFetchingId(reviewId)
    try {
      const token = localStorage.getItem('jwt_token')
      const res = await fetch(`${API_BASE}/reviews/${companyId}/${reviewId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      })
      if (res.ok) {
        const data = await res.json()
        const snap: InputSnapshot | null = data.review?.input_snapshot ?? null
        setSnapshotsById(prev => ({ ...prev, [reviewId]: snap }))
      } else {
        setSnapshotsById(prev => ({ ...prev, [reviewId]: null }))
      }
    } catch {
      setSnapshotsById(prev => ({ ...prev, [reviewId]: null }))
    } finally {
      setSnapshotFetchingId(null)
    }
  }, [companyId, snapshotsById])

  useEffect(() => { loadReviews() }, [loadReviews])

  const handleGenerate = async () => {
    if (!companyId || !cycleStart || !cycleEnd) return
    setGenerating(true)
    setError(null)
    try {
      const token = localStorage.getItem('jwt_token')
      const res = await fetch(`${API_BASE}/reviews/${companyId}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ cycle_start: cycleStart, cycle_end: cycleEnd })
      })
      if (res.ok) {
        const data = await res.json()
        await loadReviews()
        setExpandedReviewId(data.review.id)
        fetchSnapshotForReview(data.review.id)
      } else {
        const err = await res.json()
        setError(err.detail || 'Failed to generate review')
      }
    } catch (e: any) {
      setError(e.message || 'Network error')
    } finally {
      setGenerating(false)
    }
  }

  const toggleSection = (key: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleStartNewCycle = async (review: CycleReview) => {
    if (!companyId) return
    setStartingCycle(true)
    try {
      const token = localStorage.getItem('jwt_token')
      const res = await fetch(`${API_BASE}/comprehensive/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          user_query: 'Start new strategy cycle based on previous review findings',
          company_id: companyId,
          user_id: 1,
          conversation_id: Date.now(),
          seed_from_review_id: review.id,
        }),
      })
      if (res.ok) {
        setError(null)
        window.dispatchEvent(new CustomEvent('omap:new-cycle-started', {
          detail: { review_id: review.id },
        }))
      } else {
        const err = await res.json()
        setError(err.detail || 'Failed to start new cycle')
      }
    } catch (e: any) {
      setError(e.message || 'Network error')
    } finally {
      setStartingCycle(false)
    }
  }

  const handleRegenerate = async (review: CycleReview) => {
    if (!companyId) return
    setRowBusyId(review.id)
    setRowError(null)
    try {
      const token = localStorage.getItem('jwt_token')
      const res = await fetch(`${API_BASE}/reviews/${companyId}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          cycle_start: review.cycle_start,
          cycle_end: review.cycle_end,
        }),
      })
      if (res.ok) {
        const data = await res.json()
        await loadReviews()
        setExpandedReviewId(data.review.id)
        fetchSnapshotForReview(data.review.id)
      } else {
        const err = await res.json().catch(() => ({}))
        setRowError({ id: review.id, message: err.detail || 'Failed to regenerate review' })
      }
    } catch (e: any) {
      setRowError({ id: review.id, message: e.message || 'Network error' })
    } finally {
      setRowBusyId(null)
    }
  }

  const handleDelete = async (review: CycleReview) => {
    if (!companyId) return
    const confirmed = window.confirm('Delete this review? This action cannot be undone.')
    if (!confirmed) return
    setRowBusyId(review.id)
    setRowError(null)
    try {
      const token = localStorage.getItem('jwt_token')
      const res = await fetch(`${API_BASE}/reviews/${companyId}/${review.id}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      })
      if (res.ok) {
        setReviews(prev => prev.filter(r => r.id !== review.id))
        if (expandedReviewId === review.id) setExpandedReviewId(null)
      } else {
        const err = await res.json().catch(() => ({}))
        setRowError({ id: review.id, message: err.detail || 'Failed to delete review' })
      }
    } catch (e: any) {
      setRowError({ id: review.id, message: e.message || 'Network error' })
    } finally {
      setRowBusyId(null)
    }
  }

  const handleExportPdf = async (review: CycleReview) => {
    const brand = getAdvisorBrand()
    if (!brand) {
      setBrandPanelExpanded(true)
      setPdfErrorById((prev) => ({
        ...prev,
        [review.id]: 'Set your firm name first; export will resume after Save.',
      }))
      return
    }
    const node = rowContentRefs.current[review.id]
    if (!node) {
      setPdfErrorById((prev) => ({ ...prev, [review.id]: 'Review content not ready' }))
      return
    }
    setPdfExportingId(review.id)
    setPdfErrorById((prev) => {
      const { [review.id]: _drop, ...rest } = prev
      return rest
    })
    // Force-expand every section so the PDF captures all 6 (or 7 v1) bodies.
    // Collapsed sections render only the header in html2pdf, which gives the
    // reader nothing of value. Save prior state and restore after export.
    const prevExpanded = expandedSections
    const allKeys = new Set<string>([
      ...V2_SECTIONS.map((s) => s.key),
      ...V1_SECTIONS.map((s) => s.key),
    ])
    setExpandedSections(allKeys)
    // Wait for React to re-render the now-expanded section bodies before
    // html2canvas captures the DOM. 100ms matches AnalysisReportTab's pattern.
    await new Promise((resolve) => setTimeout(resolve, 100))
    try {
      await exportCycleReviewPdf({
        review,
        brand,
        clientName: 'Better Hearing For You',
        contentNode: node,
      })
    } catch (e: any) {
      setPdfErrorById((prev) => ({ ...prev, [review.id]: e?.message ?? 'Export failed' }))
    } finally {
      setExpandedSections(prevExpanded)
      setPdfExportingId(null)
    }
  }

  // A row is "superseded" when another row exists with the same (cycle_start,
  // cycle_end) and a later created_at — i.e. a more recent regeneration of the
  // same period. Latest-per-period wins; older runs render desaturated.
  const supersededIds = useMemo(() => {
    const latestPerPeriod = new Map<string, { id: number; createdAt: number }>()
    for (const r of reviews) {
      const key = `${r.cycle_start}|${r.cycle_end}`
      const createdAt = r.created_at ? new Date(r.created_at).getTime() : 0
      const current = latestPerPeriod.get(key)
      if (!current || createdAt > current.createdAt) {
        latestPerPeriod.set(key, { id: r.id, createdAt })
      }
    }
    const latestIds = new Set(Array.from(latestPerPeriod.values()).map(v => v.id))
    const out = new Set<number>()
    for (const r of reviews) {
      if (!latestIds.has(r.id)) out.add(r.id)
    }
    return out
  }, [reviews])

  if (!companyId) {
    return <div className="review-tab"><p className="review-empty">Select a company to view cycle reviews.</p></div>
  }

  return (
    <div className="review-tab">
      <div className="review-header">
        <h2>Cross-Cycle Strategic Review</h2>
        <p className="review-intro">
          This review synthesizes everything that happened during a strategy cycle — the strategic
          sessions you explored, how initiatives executed, whether your causal assumptions held, and
          where your company's health stands relative to industry norms. Use it to understand what
          worked, what didn't, and what to focus on next. Click "Start New Cycle" in the Blueprint
          section to carry these insights into your next planning cycle.
        </p>
      </div>

      {/* Generate Review Form */}
      <div className="review-generate-form">
        <h3>Generate New Review</h3>
        <div className="form-row">
          <label>
            Cycle Start
            <input type="date" value={cycleStart} onChange={e => setCycleStart(e.target.value)} />
          </label>
          <label>
            Cycle End
            <input type="date" value={cycleEnd} onChange={e => setCycleEnd(e.target.value)} />
          </label>
          <button
            className="btn-generate"
            onClick={handleGenerate}
            disabled={generating || !cycleStart || !cycleEnd || isSampleCompany}
            title={isSampleCompany ? 'Generating new reviews is disabled for the Sample Company' : undefined}
          >
            {generating ? (
              <>
                <span className="regen-spinner" aria-hidden="true" />
                Generating...
              </>
            ) : 'Generate Review'}
          </button>
        </div>
        {generating && (
          <div className="review-generate-busy" role="status" aria-live="polite">
            Synthesizing the cross-cycle review. This can take one or two minutes.
          </div>
        )}
        {error && <div className="review-error">{error}</div>}
      </div>

      <AdvisorBrandPanel
        defaultExpanded={brandPanelExpanded}
        onSaved={() => setBrandPanelExpanded(false)}
      />

      {/* Review History */}
      {reviews.length > 0 && (
        <div className="review-history">
          <div className="review-history-header">
            <h3>Review History</h3>
            <span className="review-history-hint">Click a review to expand details</span>
          </div>
          <div className="review-list-scroll">
            {reviews.map(review => {
              const isExpanded = expandedReviewId === review.id
              const isBusy = rowBusyId === review.id
              const isSuperseded = supersededIds.has(review.id)
              const rd = review.review_data
              const v2 = rd && isV2Review(rd)
              const sections = v2 ? V2_SECTIONS : V1_SECTIONS
              const grade = v2 ? rd?.executive_summary?.cycle_grade : null
              const rationale = v2 ? rd?.executive_summary?.grade_rationale : null
              const runDate = review.created_at
                ? new Date(review.created_at).toLocaleString(undefined, {
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit',
                  })
                : ''
              return (
                <div
                  key={review.id}
                  className={
                    `review-row${isExpanded ? ' expanded' : ''}${isBusy ? ' busy' : ''}${isSuperseded ? ' superseded' : ''}`
                  }
                >
                  <div
                    className="review-row-header"
                    role="button"
                    tabIndex={isBusy ? -1 : 0}
                    aria-expanded={isExpanded}
                    aria-disabled={isBusy || undefined}
                    onClick={() => {
                      if (isBusy) return
                      const next = isExpanded ? null : review.id
                      setExpandedReviewId(next)
                      if (next !== null) fetchSnapshotForReview(next)
                    }}
                    onKeyDown={e => {
                      if (isBusy) return
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        const next = isExpanded ? null : review.id
                        setExpandedReviewId(next)
                        if (next !== null) fetchSnapshotForReview(next)
                      }
                    }}
                  >
                    <span className="row-period">
                      {review.cycle_start} — {review.cycle_end}
                    </span>
                    {grade && (
                      <span className={`row-grade-pill ${getGradePillClass(grade)}${isSuperseded ? ' superseded' : ''}`}>
                        {grade}
                      </span>
                    )}
                    <span className="row-headline">
                      {isBusy
                        ? <span className="row-headline-busy">Regenerating… this can take 30–60 seconds</span>
                        : (rationale || <span className="row-headline-empty">Legacy review</span>)}
                    </span>
                    <span className="row-run-date">Run date: {runDate}</span>
                    <span className="row-actions" onClick={e => e.stopPropagation()}>
                      {review.drifted === true && (
                        <span
                          className="drift-indicator"
                          role="status"
                          aria-label="Data has changed; regeneration recommended"
                          title="Data has changed since this review was generated. Consider regenerating."
                        >
                          {'\u26A0'}
                        </span>
                      )}
                      <button
                        className="row-action regen"
                        title={
                          isSampleCompany
                            ? 'Regeneration is disabled for the Sample Company'
                            : isBusy
                              ? 'Regenerating…'
                              : 'Regenerate review'
                        }
                        disabled={isBusy || isSampleCompany}
                        onClick={() => handleRegenerate(review)}
                      >
                        {isBusy
                          ? <span className="regen-spinner" aria-label="Regenerating" />
                          : '\u27F3'}
                      </button>
                      <button
                        className="row-action trash"
                        title={
                          isSampleCompany
                            ? 'Delete is disabled for the Sample Company'
                            : 'Delete review'
                        }
                        disabled={isBusy || isSampleCompany}
                        onClick={() => handleDelete(review)}
                      >
                        {'\u{1F5D1}'}
                      </button>
                    </span>
                  </div>
                  {rowError?.id === review.id && (
                    <div className="review-row-error">{rowError.message}</div>
                  )}
                  {isExpanded && rd && (
                    <div
                      className="review-row-panel"
                      ref={(el) => { rowContentRefs.current[review.id] = el }}
                    >
                      <div className="review-row-export">
                        <button
                          type="button"
                          className="btn btn--primary export-cycle-pdf"
                          onClick={() => handleExportPdf(review)}
                          disabled={pdfExportingId === review.id}
                        >
                          {pdfExportingId === review.id
                            ? 'Generating PDF...'
                            : 'Export Advisor-Branded PDF'}
                        </button>
                        {pdfErrorById[review.id] && (
                          <div className="review-row-pdf-error">{pdfErrorById[review.id]}</div>
                        )}
                      </div>
                      <h4 className="review-row-panel-title">
                        Review: {review.cycle_start} — {review.cycle_end}
                        {!v2 && <span className="legacy-badge">Legacy format</span>}
                      </h4>
                      {v2 && rd.executive_summary && (
                        <ExecutiveSummary data={rd.executive_summary} />
                      )}
                      <div className="review-sections">
                        {sections.map(({ key, label, icon }) => (
                          <div
                            key={key}
                            className={`review-section ${expandedSections.has(key) ? 'expanded' : ''}`}
                          >
                            <button className="section-header" onClick={() => toggleSection(key)}>
                              <span>{icon} {label}</span>
                              <span className="expand-icon">
                                {expandedSections.has(key) ? '\u25BE' : '\u25B8'}
                              </span>
                            </button>
                            {expandedSections.has(key) && (
                              <div className="section-content">
                                {v2
                                  ? renderV2Section(key, rd[key], {
                                      onStartNewCycle: key === 'blueprint'
                                        ? () => handleStartNewCycle(review)
                                        : undefined,
                                      startingCycle,
                                    })
                                  : renderV1Section(key, rd[key])}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {snapshotFetchingId === review.id && (
                        <div className="dsp-loading">Loading data sources...</div>
                      )}
                      {snapshotsById[review.id] && (
                        <DataSourcesPanel snapshot={snapshotsById[review.id] as InputSnapshot} />
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {loading && <div className="review-loading">Loading reviews...</div>}
    </div>
  )
}
